/** Layer and object conventions for VeriVerse maps (spec §6.9.1). */

export const MAP_LIMITS = {
  maxTilesPerSide: 256,
  maxLayers: 12,
  maxObjects: 2_000,
} as const;

/** Layers every published map must contain, with their Tiled layer type. */
export const REQUIRED_LAYERS = {
  ground: 'tilelayer',
  walls: 'tilelayer',
  decor_below: 'tilelayer',
  decor_above: 'tilelayer',
  collision: 'tilelayer',
  objects: 'objectgroup',
  zones: 'objectgroup',
} as const;

/** Optional layers: `sprites` (ADR-0022) and the environment layers (Phase 4). */
export const OPTIONAL_LAYERS = {
  sprites: 'objectgroup',
  animations: 'objectgroup',
  outdoor: 'tilelayer',
  windows: 'objectgroup',
  fog: 'objectgroup',
} as const;

/**
 * Sprite objects (ADR-0022): props taller than a tile, drawn as one picture and depth-sorted
 * against characters. They live on the `sprites` layer and all have the type `sprite`.
 */
export const SPRITES_LAYER = 'sprites';
export const SPRITE_TYPE = 'sprite';
/** Largest sprite, in tiles per side. */
export const SPRITE_MAX_TILES = 16;

/**
 * Animated objects (ADR-0040): a looping picture from the catalog in `animated-objects.ts`, drawn
 * on the floor like a sprite but never blocking. They live on the `animations` layer and all have
 * the type `animation`.
 */
export const ANIMATIONS_LAYER = 'animations';
export const ANIMATION_TYPE = 'animation';

/** Window rectangles that show the outside light (spec §6.10). */
export const WINDOWS_LAYER = 'windows';
/**
 * Fog rectangles: a light, slowly drifting mist drawn over areas players cannot reach. They live on
 * the optional `fog` layer, are purely cosmetic and never block.
 */
export const FOG_LAYER = 'fog';
export const FOG_TYPE = 'fog';
/** Tile layer marking outdoor tiles: any non-empty tile is outdoors (spec §6.9.1). */
export const OUTDOOR_LAYER = 'outdoor';
/** Glow radius of a lamp in tiles when its `radius` property is not set. */
export const DEFAULT_LAMP_RADIUS = 3;

/** Tile layers drawn by the client, bottom to top. Characters are drawn between below and above. */
export const RENDERED_TILE_LAYERS = ['ground', 'walls', 'decor_below', 'decor_above'] as const;

/** Tile layers whose non-empty tiles block movement. */
export const BLOCKING_TILE_LAYERS = ['walls', 'collision'] as const;

/** Interactive object types (spec §6.6). Unknown types are rejected so typos surface at upload time. */
export const OBJECT_TYPES = [
  'desk',
  'chair',
  'computer',
  'door',
  'window',
  'beer_tap',
  'coffee_machine',
  'fridge',
  'candy_bowl',
  'wall_calendar',
  'whiteboard',
  'bulletin_board',
  'wheel',
  'ping_pong_table',
  'dance_floor',
  'cat_spot',
  'cake_spot',
  'teleporter',
  'coop_door',
  'toggle_door',
  'pin_door',
  'lamp',
] as const;
export type ObjectType = (typeof OBJECT_TYPES)[number];

/**
 * Object types allowed on the `zones` layer. A `region` is a separate area of a map, such as one
 * office of several: the camera stays inside the region the player stands in.
 */
export const ZONE_OBJECT_TYPES = ['zone', 'spawn', 'region'] as const;
export type ZoneObjectType = (typeof ZONE_OBJECT_TYPES)[number];

/** Tiled stores flip/rotation flags in the top bits of a gid; mask them off to get the tile id. */
export const GID_MASK = 0x1fffffff;
